import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ThemeFromServerContext as ThemeFromServerContextBase,
  THEME_COOKIE_NAME as THEME_COOKIE_NAME_IMPORT,
} from '~/shared/theme-server-context';
import type { ThemeModePreference } from '~/shared/theme-server-context';

export type { ThemeModePreference } from '~/shared/theme-server-context';
export type ThemeMode = 'light' | 'dark';
export {
  ThemeFromServerContextBase as ThemeFromServerContext,
  THEME_COOKIE_NAME_IMPORT as THEME_COOKIE_NAME,
};

const THEME_STORAGE_KEY = 'theme-mode';

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveEffectiveMode(preference: ThemeModePreference): ThemeMode {
  if (preference === 'system') {
    return getSystemPrefersDark() ? 'dark' : 'light';
  }
  return preference;
}

function readInitialPreference(): ThemeModePreference {
  if (typeof window === 'undefined') {
    return 'light';
  }
  const w = window as unknown as { __INITIAL_THEME_MODE__?: string };
  if (
    w.__INITIAL_THEME_MODE__ === 'dark' ||
    w.__INITIAL_THEME_MODE__ === 'light' ||
    w.__INITIAL_THEME_MODE__ === 'system'
  ) {
    return w.__INITIAL_THEME_MODE__;
  }
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed === 'dark' || parsed === 'light' || parsed === 'system') {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return 'light';
}

interface ThemeContextType {
  /** User preference: light, dark, or system */
  mode: ThemeModePreference;
  /** Resolved mode for applying theme (light or dark). When mode is 'system', follows OS. */
  effectiveMode: ThemeMode;
  setMode: (mode: ThemeModePreference) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  effectiveMode: 'light',
  setMode: () => {},
  toggleMode: () => {},
});

const THEME_COOKIE_MAX_AGE_DAYS = 365;

export function setThemeCookie(preference: ThemeModePreference): void {
  if (typeof document === 'undefined') {
    return;
  }
  const value = encodeURIComponent(JSON.stringify(preference));
  document.cookie = `${THEME_COOKIE_NAME_IMPORT}=${value}; path=/; max-age=${THEME_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60}; SameSite=Lax`;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

function getInitialResolvedFromWindow(): ThemeMode | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const w = window as unknown as { __INITIAL_RESOLVED_THEME__?: string };
  if (
    w.__INITIAL_RESOLVED_THEME__ === 'dark' ||
    w.__INITIAL_RESOLVED_THEME__ === 'light'
  ) {
    return w.__INITIAL_RESOLVED_THEME__;
  }
  return undefined;
}

// Lock initial resolved theme for first paint so hydration matches server (read once, use until mounted).
const initialResolvedForPaintRef: { value: ThemeMode | null } = { value: null };

function getInitialResolvedForPaint(
  themeFromServer: { resolved: ThemeMode } | undefined
): ThemeMode | null {
  if (initialResolvedForPaintRef.value !== null) {
    return initialResolvedForPaintRef.value;
  }
  const resolved = themeFromServer?.resolved ?? getInitialResolvedFromWindow();
  if (resolved) {
    initialResolvedForPaintRef.value = resolved;
    return resolved;
  }
  return null;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const themeFromServer = useContext(ThemeFromServerContextBase);
  const [mode, setModeState] = useState<ThemeModePreference>(() =>
    typeof window === 'undefined'
      ? (themeFromServer?.preference ?? 'light')
      : readInitialPreference()
  );
  const [systemDark, setSystemDark] = useState(getSystemPrefersDark);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setThemeCookie(mode);
  }, [mode]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = () => setSystemDark(mq.matches);
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  const effectiveMode = useMemo((): ThemeMode => {
    const initialResolved = getInitialResolvedForPaint(themeFromServer);
    if (!hasMounted && initialResolved) {
      return initialResolved;
    }
    if (mode === 'system') {
      return systemDark ? 'dark' : 'light';
    }
    return mode;
  }, [hasMounted, themeFromServer, mode, systemDark]);

  const setMode = useCallback((next: ThemeModePreference) => {
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(next));
      setThemeCookie(next);
    } catch {
      // ignore
    }
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev: ThemeModePreference) => {
      const next: ThemeModePreference =
        prev === 'system'
          ? getSystemPrefersDark()
            ? 'light'
            : 'dark'
          : prev === 'dark'
            ? 'light'
            : 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(next));
        setThemeCookie(next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const value: ThemeContextType = {
    mode,
    effectiveMode,
    setMode,
    toggleMode,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export function useThemeMode(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
}

export default ThemeContext;
